import { UrlFetchResult } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Validates if a string is a valid URL
 */
export function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Attempts to fetch page content via our backend proxy
 * Falls back to suggesting manual paste if it fails
 */
export async function fetchPageContent(url: string): Promise<UrlFetchResult> {
  if (!isValidUrl(url)) {
    return {
      success: false,
      error: 'Invalid URL format. Please enter a valid HTTP/HTTPS URL.',
    };
  }

  try {
    const proxyUrl = `${API_BASE_URL}/api/proxy?url=${encodeURIComponent(url)}`;
    const response = await fetch(proxyUrl);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `Failed to fetch: ${response.statusText}`;
      console.warn('Proxy error:', errorMsg);
      return {
        success: false,
        error: errorMsg,
        requiresManualPaste: true,
      };
    }

    const data = await response.json();
    const content = data.content;

    // Basic validation that we got HTML content
    if (!content || content.length < 100) {
      return {
        success: false,
        error: 'Page content too short or empty. The website may require JavaScript.',
        requiresManualPaste: true,
      };
    }

    // Extract image URL from HTML before stripping tags
    const imageUrl = extractImageUrl(content, url);

    // Strip HTML tags and extract text content for AI processing
    const textContent = extractTextContent(content);

    return {
      success: true,
      content: textContent,
      imageUrl: imageUrl || undefined,
    };
  } catch (error: any) {
    console.warn('Proxy fetch failed:', error);
    return {
      success: false,
      error: error.message || 'Unable to fetch the page content.',
      requiresManualPaste: true,
    };
  }
}

/**
 * Extracts the best product image URL from HTML
 * Checks og:image, twitter:image, and product image patterns
 */
function extractImageUrl(html: string, pageUrl: string): string | null {
  // Try og:image meta tag first (most reliable)
  const ogMatch = html.match(/<meta[^>]*property=["']og:image["'][^>]*content=["']([^"']+)["']/i)
    || html.match(/<meta[^>]*content=["']([^"']+)["'][^>]*property=["']og:image["']/i);
  if (ogMatch?.[1]) {
    return resolveUrl(ogMatch[1], pageUrl);
  }

  // Try twitter:image
  const twitterMatch = html.match(/<meta[^>]*name=["']twitter:image["'][^>]*content=["']([^"']+)["']/i)
    || html.match(/<meta[^>]*content=["']([^"']+)["'][^>]*name=["']twitter:image["']/i);
  if (twitterMatch?.[1]) {
    return resolveUrl(twitterMatch[1], pageUrl);
  }

  return null;
}

/**
 * Resolves a potentially relative URL against a base URL
 */
function resolveUrl(url: string, base: string): string {
  try {
    return new URL(url, base).href;
  } catch {
    return url;
  }
}

/**
 * Extracts text content from HTML, removing scripts, styles, and normalizing whitespace
 */
function extractTextContent(html: string): string {
  // Remove script and style tags with their content
  let text = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  text = text.replace(/<noscript[^>]*>[\s\S]*?<\/noscript>/gi, '');

  // Remove HTML comments
  text = text.replace(/<!--[\s\S]*?-->/g, '');

  // Replace common block elements with newlines
  text = text.replace(/<\/(p|div|h[1-6]|li|tr|br|hr)[^>]*>/gi, '\n');
  text = text.replace(/<br[^>]*\/?>/gi, '\n');

  // Remove remaining HTML tags
  text = text.replace(/<[^>]+>/g, ' ');

  // Decode HTML entities
  text = decodeHtmlEntities(text);

  // Normalize whitespace
  text = text.replace(/[ \t]+/g, ' ');
  text = text.replace(/\n[ \t]+/g, '\n');
  text = text.replace(/[ \t]+\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
}

/**
 * Decodes common HTML entities
 */
function decodeHtmlEntities(text: string): string {
  const entities: Record<string, string> = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&mdash;': '—',
    '&ndash;': '–',
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
    '&times;': '×',
    '&divide;': '÷',
  };

  let decoded = text;
  for (const [entity, char] of Object.entries(entities)) {
    decoded = decoded.replace(new RegExp(entity, 'gi'), char);
  }

  // Decode numeric entities
  decoded = decoded.replace(/&#(\d+);/g, (_, num) =>
    String.fromCharCode(parseInt(num, 10))
  );
  decoded = decoded.replace(/&#x([a-fA-F0-9]+);/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

  return decoded;
}

/**
 * Prepares manual content for AI processing
 * Used when user pastes content directly
 */
export function prepareManualContent(content: string): string {
  // If content looks like HTML, extract text
  if (content.includes('<') && content.includes('>')) {
    return extractTextContent(content);
  }

  // Otherwise, just normalize whitespace
  return content
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
